import { Platform } from 'react-native';

import LS from '../localStorageUtils';

/**
 * Stable per-install device identifier sent to gateway-game as the
 * `player-device-hash` header (login, registration, social login, refresh) and in the
 * game-socket handshake. The back office uses it for device bans, so it must survive
 * logout and app restarts:
 *  - native: react-native-device-info's unique id (Android ID / iOS identifierForVendor)
 *  - web: a random id persisted in localStorage
 * The value is opaque to the server; it is never shown to the player.
 */
const STORAGE_KEY = 'device_hash';

let cached: string | null = null;

const randomId = (): string => {
	const cryptoApi = (globalThis as any)?.crypto;

	if (typeof cryptoApi?.randomUUID === 'function') {
		return cryptoApi.randomUUID();
	}

	if (typeof cryptoApi?.getRandomValues === 'function') {
		const bytes = new Uint8Array(16);
		cryptoApi.getRandomValues(bytes);
		return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
	}

	return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}${Math.random()
		.toString(16)
		.slice(2)}`;
};

const nativeUniqueId = (): string | null => {
	if (Platform.OS === 'web') {
		return null;
	}

	try {
		const DeviceInfo = require('react-native-device-info');
		const getter = DeviceInfo?.getUniqueIdSync ?? DeviceInfo?.default?.getUniqueIdSync;
		const id = typeof getter === 'function' ? getter() : null;

		return typeof id === 'string' && id.length > 0 && id !== 'unknown' ? id : null;
	} catch (error) {
		console.warn('[deviceHash] native unique id unavailable', error);
		return null;
	}
};

/** Synchronous accessor; generates and persists an id on first use when needed. */
export const getDeviceHash = (): string => {
	if (cached) {
		return cached;
	}

	let id = nativeUniqueId();

	if (!id) {
		id = LS.get(STORAGE_KEY);
	}

	if (!id) {
		id = randomId();
		LS.set(STORAGE_KEY, id);
	}

	cached = id;
	return id;
};

/**
 * Resolves the persisted fallback id from async storage on native before the first
 * request goes out, so a device without a native unique id keeps one hash across restarts.
 */
export const primeDeviceHash = async (): Promise<string> => {
	if (cached) {
		return cached;
	}

	const nativeId = nativeUniqueId();
	if (nativeId) {
		cached = nativeId;
		return nativeId;
	}

	try {
		const stored = await LS.getAsync(STORAGE_KEY);
		if (stored) {
			cached = stored;
			return stored;
		}
	} catch (error) {
		console.warn('[deviceHash] failed to read persisted id', error);
	}

	return getDeviceHash();
};

primeDeviceHash().catch(error => {
	console.warn('[deviceHash] prime failed', error);
});
